import { errorMessage } from './httpClient';

const SOURCE_SAVE_FALLBACK = '来源表单保存失败，请检查表单后重试';
const FORM_VALIDATION_MESSAGE = '请检查来源表单中标出的必填或格式错误';

export class SourceApplicationSaveError extends Error {
  constructor(readonly reason: unknown) {
    super(SOURCE_SAVE_FALLBACK);
    this.name = 'SourceApplicationSaveError';
  }
}

export async function withSourceSaveError<T>(save: () => Promise<T>): Promise<T> {
  try {
    return await save();
  } catch (reason) {
    throw new SourceApplicationSaveError(reason);
  }
}

export function sourceActionErrorMessage(error: unknown): string {
  return error instanceof SourceApplicationSaveError
    ? sourceSaveErrorMessage(error.reason)
    : errorMessage(error);
}

export function sourceSaveErrorMessage(reason: unknown): string {
  if (isFormValidationFailure(reason)) return FORM_VALIDATION_MESSAGE;

  const code = safeCode(ownString(reason, 'code'));
  const traceId = safeTraceId(ownString(reason, 'traceId'));
  const message = safeMessage(ownString(reason, 'message'));
  // 来源错误可跨 Wujie Realm 传入，不能用 instanceof Error 判定。
  const trustedEnvelope = Boolean(code);
  const display = trustedEnvelope || (isErrorLike(reason) && userFacingMessage(message))
    ? message : undefined;
  const suffix = trustedEnvelope
    ? [code, traceId ? `traceId: ${traceId}` : undefined].filter(Boolean).join('，')
    : undefined;
  if (display) return suffix ? `${display}（${suffix}）` : display;
  return suffix ? `${SOURCE_SAVE_FALLBACK}（${suffix}）` : SOURCE_SAVE_FALLBACK;
}

function isFormValidationFailure(value: unknown): boolean {
  const errors = ownValue(value, 'errors');
  return Boolean(errors && typeof errors === 'object' && !Array.isArray(errors));
}

function isErrorLike(value: unknown): boolean {
  try {
    return Object.prototype.toString.call(value) === '[object Error]';
  } catch {
    return false;
  }
}

function ownString(value: unknown, key: string): string | undefined {
  const selected = ownValue(value, key);
  return typeof selected === 'string' ? selected : undefined;
}

function ownValue(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object') return undefined;
  try {
    const property = Object.getOwnPropertyDescriptor(value, key);
    return property && 'value' in property ? property.value : undefined;
  } catch {
    return undefined;
  }
}

function safeCode(value: string | undefined): string | undefined {
  return value && (/^[A-Z][A-Z0-9_]{2,100}$/.test(value) || /^\d{6,12}$/.test(value))
    ? value : undefined;
}

function safeTraceId(value: string | undefined): string | undefined {
  return value && /^[A-Za-z0-9_-]{8,128}$/.test(value) ? value : undefined;
}

function safeMessage(value: string | undefined): string | undefined {
  if (!value || value !== value.trim() || value.length > 160
      || /[\r\n\x00-\x1f<>{}`]/.test(value)
      || /https?:\/\/|file:\/\/|\/(?:api|internal)\/|[A-Za-z]:\\|(?:Bearer|token|password|secret|credential|private.?key|stack|exception|\bSQL\b|\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b)/i.test(value)) {
    return undefined;
  }
  return value;
}

function userFacingMessage(value: string | undefined): boolean {
  return Boolean(value && /^(请|当前|来源表单|受理表单|设计人员候选|读取设计人员候选)/.test(value));
}
